import axios from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { graphQLClient } from 'graphql/graphQLClient';
import { PublishProductMutation } from 'hooks/usePublishProduct';

const UploadArticlesPublishContext = createContext(null);

const yieldToMain = () =>
  new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

export function UploadArticlesPublishProvider({ children }) {
  const [publishing, setPublishing] = useState(false);
  const [processingArticles, setProcessingArticles] = useState(() => new Map());
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [currentProcessing, setCurrentProcessing] = useState(0);
  const [publishJobError, setPublishJobError] = useState(null);

  const publishCancelledRef = useRef(false);
  const publishClearTimeoutRef = useRef(null);
  const listSyncRef = useRef(null);
  const publishRunLockRef = useRef(false);

  const clearPublishProcessingDelay = useCallback(() => {
    if (publishClearTimeoutRef.current) {
      clearTimeout(publishClearTimeoutRef.current);
      publishClearTimeoutRef.current = null;
    }
  }, []);

  const registerListSync = useCallback((handlers) => {
    listSyncRef.current = handlers;
    return () => {
      listSyncRef.current = null;
    };
  }, []);

  const cancelPublish = useCallback(() => {
    publishCancelledRef.current = true;
  }, []);

  const startPublishArticles = useCallback(
    (articlesToPublish) => {
      if (!articlesToPublish?.length) return;
      if (publishRunLockRef.current) return;

      publishRunLockRef.current = true;
      publishCancelledRef.current = false;
      clearPublishProcessingDelay();
      setPublishJobError(null);
      setPublishing(true);

      void (async () => {
        try {
          const markRemainingCancelled = (fromIndex) => {
            setProcessingArticles((prev) => {
              const newMap = new Map(prev);
              for (let j = fromIndex; j < articlesToPublish.length; j++) {
                const aid = articlesToPublish[j].id;
                const st = newMap.get(aid);
                if (
                  st &&
                  st.status !== 'completed' &&
                  st.status !== 'error'
                ) {
                  newMap.set(aid, { status: 'cancelled', progress: 0 });
                }
              }
              return newMap;
            });
          };

          const initialProcessingState = new Map();
          articlesToPublish.forEach((article) => {
            initialProcessingState.set(article.id, {
              status: 'pending',
              progress: 0,
            });
          });
          setProcessingArticles(initialProcessingState);
          setTotalToProcess(articlesToPublish.length);
          setCurrentProcessing(0);

          const publishResults = [];
          let stoppedByCancel = false;

          for (let i = 0; i < articlesToPublish.length; i++) {
            await yieldToMain();

            if (publishCancelledRef.current) {
              markRemainingCancelled(i);
              stoppedByCancel = true;
              break;
            }

            const article = articlesToPublish[i];

            setProcessingArticles((prev) => {
              const newMap = new Map(prev);
              newMap.set(article.id, { status: 'processing', progress: 0 });
              return newMap;
            });
            setCurrentProcessing(i + 1);

            if (
              !article.ml_product_id ||
              !article.calculated_price ||
              !article.category_id
            ) {
              setProcessingArticles((prev) => {
                const newMap = new Map(prev);
                newMap.set(article.id, { status: 'error', progress: 100 });
                return newMap;
              });
              publishResults.push({
                asin: article.asin,
                success: false,
                error:
                  'Faltan datos requeridos (ml_product_id, calculated_price o category_id)',
              });
              continue;
            }

            const payload = {
              productId: article.ml_product_id,
              currency: 'UYU',
              price: article.calculated_price,
              stock: 12,
              categoryId: article.category_id,
              sku: article.asin,
            };

            try {
              setProcessingArticles((prev) => {
                const newMap = new Map(prev);
                newMap.set(article.id, { status: 'processing', progress: 50 });
                return newMap;
              });

              const result = await graphQLClient.request({
                query: PublishProductMutation,
                variables: payload,
              });

              const data = result.data;
              const gqlErr =
                result.error?.graphQLErrors?.[0]?.message ||
                result.error?.fetchError?.message;

              if (gqlErr || result.error?.httpError) {
                setProcessingArticles((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(article.id, { status: 'error', progress: 100 });
                  return newMap;
                });
                publishResults.push({
                  asin: article.asin,
                  success: false,
                  error: gqlErr || 'Error de red o GraphQL',
                });
                continue;
              }

              if (data?.publishProductFromCatalog?.error) {
                setProcessingArticles((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(article.id, { status: 'error', progress: 100 });
                  return newMap;
                });
                publishResults.push({
                  asin: article.asin,
                  success: false,
                  error: data.publishProductFromCatalog.error,
                });
              } else {
                setProcessingArticles((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(article.id, { status: 'processing', progress: 75 });
                  return newMap;
                });

                try {
                  await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/update-item-status`,
                    {
                      id: article.id,
                      status: true,
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${
                          JSON.parse(localStorage?.session)?.sessionInfo?.token
                        }`,
                      },
                    }
                  );
                  listSyncRef.current?.markPublished?.(article.id);
                } catch (updateError) {
                  console.error(
                    `Error actualizando estado del artículo ${article.id}:`,
                    updateError
                  );
                }

                setProcessingArticles((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(article.id, {
                    status: 'completed',
                    progress: 100,
                  });
                  return newMap;
                });

                publishResults.push({
                  asin: article.asin,
                  success: true,
                  id: data?.publishProductFromCatalog?.id,
                });
              }
            } catch (err) {
              setProcessingArticles((prev) => {
                const newMap = new Map(prev);
                newMap.set(article.id, { status: 'error', progress: 100 });
                return newMap;
              });
              publishResults.push({
                asin: article.asin,
                success: false,
                error: err.message || 'Error al publicar',
              });
            }
          }

          const successCount = publishResults.filter((r) => r.success).length;
          const errorCount = publishResults.filter((r) => !r.success).length;

          if (stoppedByCancel) {
            if (errorCount > 0) {
              setPublishJobError(
                `Publicación cancelada. ${successCount} publicado(s), ${errorCount} con error(es); el resto quedó sin publicar.`
              );
            } else if (successCount > 0) {
              setPublishJobError(
                `Publicación cancelada. Se publicaron ${successCount} artículo(s); el resto quedó sin publicar.`
              );
            } else {
              setPublishJobError('Publicación cancelada.');
            }
          } else if (errorCount > 0) {
            setPublishJobError(
              `${successCount} artículo(s) publicados exitosamente. ${errorCount} artículo(s) con errores.`
            );
            console.error(
              `${successCount} artículo(s) publicados exitosamente. ${errorCount} artículo(s) con errores.`
            );
          } else {
            console.log(`${successCount} artículo(s) publicados exitosamente`);
          }

          listSyncRef.current?.refreshList?.();

          if (!stoppedByCancel) {
            listSyncRef.current?.clearSelection?.();
          }

          clearPublishProcessingDelay();
          publishClearTimeoutRef.current = setTimeout(() => {
            publishClearTimeoutRef.current = null;
            setProcessingArticles(new Map());
            setTotalToProcess(0);
            setCurrentProcessing(0);
          }, 2000);
        } catch (error) {
          console.error('Error publicando artículos:', error);
          setPublishJobError('Error al publicar los artículos');
          setProcessingArticles(new Map());
          setTotalToProcess(0);
          setCurrentProcessing(0);
        } finally {
          setPublishing(false);
          publishRunLockRef.current = false;
        }
      })();
    },
    [clearPublishProcessingDelay]
  );

  const value = useMemo(
    () => ({
      publishing,
      processingArticles,
      totalToProcess,
      currentProcessing,
      publishJobError,
      startPublishArticles,
      cancelPublish,
      registerListSync,
    }),
    [
      publishing,
      processingArticles,
      totalToProcess,
      currentProcessing,
      publishJobError,
      startPublishArticles,
      cancelPublish,
      registerListSync,
    ]
  );

  return (
    <UploadArticlesPublishContext.Provider value={value}>
      {children}
    </UploadArticlesPublishContext.Provider>
  );
}

export function useUploadArticlesPublish() {
  const ctx = useContext(UploadArticlesPublishContext);
  if (!ctx) {
    throw new Error(
      'useUploadArticlesPublish debe usarse dentro de UploadArticlesPublishProvider'
    );
  }
  return ctx;
}
